<!-- ABOUTME: Email automation rules manager with split-pane layout -->
<!-- ABOUTME: Lists rules on left, shows form/details on right, displays template usage and language strategy -->
<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header Section -->
    <header class="bg-white border-b border-gray-200 py-5 px-5">
      <div class="max-w-7xl mx-auto">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 class="text-3xl font-bold text-title-dark m-0">Email Automation Rules</h1>
            <p class="text-text-dark text-sm mt-1 mb-0">Automated email sequences based on user behavior</p>
          </div>

          <button
            @click="createNewRule"
            class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md no-underline font-medium text-sm transition-opacity hover:opacity-90 cursor-pointer">
            <Plus :size="16" class="mr-2" />
            New Rule
          </button>
        </div>
      </div>
    </header>

    <!-- Main Split Layout -->
    <main class="max-w-7xl mx-auto py-6 px-5">
      <div class="flex flex-col lg:flex-row gap-6 min-h-[600px]">

        <!-- LEFT PANE: Rules List (30%) -->
        <div class="w-full lg:w-80 lg:flex-shrink-0">
          <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h2 class="text-lg font-semibold text-title-dark mb-4">Rules</h2>

            <!-- Loading State -->
            <div v-if="isLoading" class="text-center py-8 text-gray-400">
              Loading rules...
            </div>

            <!-- Empty State -->
            <div v-else-if="rules.length === 0" class="text-center py-8">
              <p class="text-gray-400 text-sm mb-4">No rules configured yet</p>
              <button
                @click="createNewRule"
                class="text-primary text-sm font-medium hover:underline cursor-pointer">
                Create your first rule
              </button>
            </div>

            <!-- Rules List -->
            <div v-else class="space-y-2">
              <div
                v-for="rule in rules"
                :key="rule.id"
                @click="selectRule(rule)"
                :class="[
                  'p-3 rounded-md border-2 cursor-pointer transition-all',
                  selectedRule?.id === rule.id
                    ? 'border-primary bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                ]">

                <!-- Rule Header -->
                <div class="flex items-start justify-between gap-2 mb-1">
                  <span class="text-sm font-semibold text-title-dark">
                    <template v-if="rule.trigger_type === 'all_users'">All Users</template>
                    <template v-else>Day {{ rule.trigger_value }} <span v-if="rule.trigger_type === 'days_after_last_activity'" class="font-normal text-xs text-gray-500">(activity)</span></template>
                  </span>
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      rule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ rule.active ? 'Active' : 'Inactive' }}
                  </span>
                </div>

                <!-- Template Key -->
                <div class="text-xs text-gray-600 mb-1 font-mono">
                  {{ rule.template_key }}
                </div>

                <!-- Condition Badge -->
                <div class="flex items-center gap-1 mb-2">
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                    {{ formatCondition(rule.condition_type) }}
                  </span>
                </div>

                <!-- Language Strategy Indicator -->
                <div class="flex items-center gap-1 text-xs text-gray-500">
                  <Globe :size="12" />
                  <span v-if="rule.missing_variant_strategy === 'fallback_to_english'">
                    Auto-match + EN fallback
                  </span>
                  <span v-else class="text-amber-600">
                    Skip if missing
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT PANE: Rule Form/Details (70%) -->
        <div class="flex-1 min-w-0">
          <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            <!-- No Selection State -->
            <div v-if="!selectedRule && !isCreatingNew" class="text-center py-16 text-gray-400">
              <FileText :size="48" class="mx-auto mb-4 opacity-50" />
              <p class="text-lg mb-2">Select a rule to view details</p>
              <p class="text-sm">or create a new one</p>
            </div>

            <!-- Rule View Mode (Read-only) -->
            <div v-else-if="selectedRule && !isEditing && !isCreatingNew">
              <div class="mb-6">
                <h2 class="text-xl font-bold text-title-dark">Rule Details</h2>
              </div>

              <!-- Rule Information -->
              <div class="space-y-4 mb-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Template</label>
                  <code class="text-sm bg-gray-100 px-2 py-1 rounded">{{ selectedRule.template_key }}</code>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Trigger</label>
                  <p class="text-sm text-gray-900" v-if="selectedRule.trigger_type === 'all_users'">All users (condition only)</p>
                  <p class="text-sm text-gray-900" v-else>{{ selectedRule.trigger_value }} days after {{ selectedRule.trigger_type === 'days_after_last_activity' ? 'last activity' : 'user signup' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Condition</label>
                  <span class="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                    {{ formatCondition(selectedRule.condition_type) }}
                  </span>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Language Strategy</label>
                  <p class="text-sm text-gray-900">{{ selectedRule.language_strategy_description }}</p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                      selectedRule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ selectedRule.active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex gap-3 mb-6">
                <button
                  @click="runTestRun"
                  :disabled="testRunning"
                  type="button"
                  class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                  <span v-if="testRunning">Running...</span>
                  <span v-else>Test Run</span>
                </button>

                <button
                  @click="startEditing"
                  type="button"
                  class="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 text-sm font-medium">
                  Edit
                </button>

                <button
                  @click="deleteRule"
                  type="button"
                  class="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium">
                  Delete
                </button>
              </div>

              <!-- Test Run Error -->
              <div v-if="testError" class="mb-6 bg-red-50 border-l-4 border-red-500 rounded-md p-4">
                <p class="text-sm text-red-700">{{ testError }}</p>
              </div>

              <!-- Test Run Results -->
              <div v-if="showTestResults && testResults" class="space-y-6">
                <div class="border-t pt-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4">Test Run Results</h3>

                  <!-- Summary Stats -->
                  <div class="grid grid-cols-3 gap-4 mb-6">
                    <div class="bg-white border border-gray-200 rounded-lg p-4">
                      <div class="text-2xl font-bold text-blue-600">{{ testResults.test_results.will_send }}</div>
                      <div class="text-sm text-gray-600">Will Send</div>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-lg p-4">
                      <div class="text-2xl font-bold text-amber-600">{{ testResults.test_results.will_skip }}</div>
                      <div class="text-sm text-gray-600">Will Skip</div>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-lg p-4">
                      <div class="text-2xl font-bold text-gray-600">{{ testResults.test_results.batch_count }}</div>
                      <div class="text-sm text-gray-600">Batches</div>
                    </div>
                  </div>

                  <!-- Locale Breakdown -->
                  <div v-if="Object.keys(testResults.test_results.locale_breakdown).length > 0" class="mb-6">
                    <h4 class="text-sm font-semibold text-gray-700 mb-2">Language Distribution</h4>
                    <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <table class="min-w-full text-sm">
                        <tbody class="divide-y divide-gray-200">
                          <tr v-for="(count, locale) in testResults.test_results.locale_breakdown" :key="locale">
                            <td class="py-2 pr-4 font-medium text-gray-900">{{ locale.toUpperCase() }}</td>
                            <td class="py-2 text-gray-600">{{ count }} emails</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Status Indicator -->
                  <div
                    :class="[
                      'rounded-lg p-4 mb-6',
                      testResults.test_results.ready_to_send
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    ]">
                    <div class="flex items-start">
                      <div class="flex-shrink-0">
                        <span v-if="testResults.test_results.ready_to_send" class="text-green-600 text-xl">✅</span>
                        <span v-else class="text-red-600 text-xl">❌</span>
                      </div>
                      <div class="ml-3">
                        <h4
                          :class="[
                            'text-sm font-semibold mb-1',
                            testResults.test_results.ready_to_send ? 'text-green-800' : 'text-red-800'
                          ]">
                          {{ testResults.test_results.ready_to_send ? 'Ready to Send' : 'Issues Detected' }}
                        </h4>
                        <div v-if="testResults.test_results.issues.length > 0">
                          <ul class="list-disc list-inside text-sm space-y-1"
                              :class="testResults.test_results.ready_to_send ? 'text-green-700' : 'text-red-700'">
                            <li v-for="(issue, idx) in testResults.test_results.issues" :key="idx">{{ issue }}</li>
                          </ul>
                        </div>
                        <p v-else class="text-sm text-green-700">No issues found. Safe to activate this rule.</p>
                      </div>
                    </div>
                  </div>

                  <!-- Sample Emails -->
                  <div v-if="testResults.test_results.sample_emails && testResults.test_results.sample_emails.length > 0">
                    <h4 class="text-sm font-semibold text-gray-700 mb-2">Sample Emails ({{ testResults.test_results.sample_emails.length }})</h4>
                    <div class="space-y-3">
                      <div
                        v-for="(sample, idx) in testResults.test_results.sample_emails"
                        :key="idx"
                        class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div class="text-xs text-gray-500 mb-1">To: {{ sample.to }}</div>
                        <div class="text-sm font-semibold text-gray-900 mb-2">{{ sample.subject }}</div>
                        <div class="text-xs text-gray-600">{{ sample.body_preview }}...</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Rule Form (Edit/Create) -->
            <div v-else-if="(selectedRule && isEditing) || isCreatingNew">
              <div class="mb-6">
                <h2 class="text-xl font-bold text-title-dark">
                  {{ isCreatingNew ? 'Create New Rule' : 'Edit Rule' }}
                </h2>
              </div>

              <!-- Error Display -->
              <div v-if="errors.length > 0" class="mb-6 bg-red-50 border-l-4 border-red-500 rounded-md p-4">
                <h4 class="text-sm font-semibold text-red-800 mb-2">
                  Please fix the following errors:
                </h4>
                <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
                  <li v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
                </ul>
              </div>

              <form @submit.prevent="saveRule" class="space-y-6">

                <!-- Template Key -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Email Template
                  </label>
                  <select
                    v-model="formData.template_key"
                    @change="onTemplateSelected"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="">-- Select Template --</option>
                    <option v-for="key in templateKeys" :key="key" :value="key">
                      {{ key }}
                    </option>
                  </select>

                  <!-- Show/Hide Preview Button -->
                  <button
                    v-if="formData.template_key"
                    @click="togglePreview"
                    type="button"
                    class="mt-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 cursor-pointer">
                    {{ showPreview ? 'Hide Preview' : 'Show Preview' }}
                  </button>
                </div>

                <!-- Email Preview (rolldown/rollup) -->
                <div v-if="formData.template_key && previewLoaded" v-show="showPreview" class="border border-gray-300 rounded-md bg-white">
                  <!-- Compact header with language selector -->
                  <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
                    <span class="text-xs font-medium text-gray-600">Email Preview</span>
                    <select
                      v-if="availableLocales.length > 1"
                      v-model="previewLocale"
                      @change="loadPreview"
                      class="text-xs px-2 py-1 border border-gray-300 rounded bg-white">
                      <option v-for="locale in availableLocales" :key="locale" :value="locale">
                        {{ locale.toUpperCase() }}
                      </option>
                    </select>
                  </div>

                  <!-- Email content - full body -->
                  <div class="p-4">
                    <div v-if="isLoadingPreview" class="text-center py-8 text-gray-400 text-sm">
                      Loading...
                    </div>
                    <div v-else-if="previewError" class="text-center py-4 text-red-600 text-sm">
                      {{ previewError }}
                    </div>
                    <div v-else>
                      <div class="mb-3 pb-3 border-b border-gray-200">
                        <div class="text-xs text-gray-500 mb-1">Subject:</div>
                        <div class="font-medium text-gray-900">{{ emailPreview.subject }}</div>
                      </div>
                      <div class="prose prose-sm max-w-none" v-html="emailPreview.body"></div>
                    </div>
                  </div>
                </div>

                <!-- Trigger Type -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Trigger Type
                  </label>
                  <select
                    v-model="formData.trigger_type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="days_after_signup">Days After Signup</option>
                    <option value="days_after_last_activity">Days After Last Activity</option>
                    <option value="all_users">All Users (condition only)</option>
                  </select>
                </div>

                <!-- Trigger Value (Days) - hidden for all_users trigger -->
                <div v-if="formData.trigger_type !== 'all_users'">
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Send After (days)
                  </label>
                  <input
                    type="number"
                    v-model.number="formData.trigger_value"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="e.g., 7">
                </div>

                <!-- Condition Type -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Send To Users Who...
                  </label>
                  <select
                    v-model="formData.condition_type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="always">Are active (always send)</option>
                    <option value="has_no_projects">Have no projects</option>
                    <option value="has_no_network">Have no network connections</option>
                    <option value="has_no_wants">Have no wants</option>
                    <option value="is_free_tier">Are on free tier</option>
                    <option value="has_draft_older_than_x_days">Have a draft project older than X days</option>
                    <option value="has_empty_bio">Have an empty bio</option>
                    <option value="is_test_user">Are test users (admins + unlisters*@ferovo.sk only)</option>
                  </select>
                </div>

                <!-- Condition Value (for parameterized conditions) -->
                <div v-if="formData.condition_type === 'has_draft_older_than_x_days' || formData.condition_type === 'has_less_than_x_connections'">
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    {{ formData.condition_type === 'has_draft_older_than_x_days' ? 'Draft older than (days)' : 'Fewer than (connections)' }}
                  </label>
                  <input
                    type="number"
                    v-model.number="formData.condition_value"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    :placeholder="formData.condition_type === 'has_draft_older_than_x_days' ? 'e.g., 7' : 'e.g., 5'">
                </div>

                <!-- Language Strategy -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    How to handle users when their language variant doesn't exist
                  </label>
                  <select
                    v-model="formData.missing_variant_strategy"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="fallback_to_english">Send English version</option>
                    <option value="skip_user">Skip user</option>
                  </select>
                </div>

                <!-- Active Status -->
                <div class="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    v-model="formData.active"
                    class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary">
                  <label for="active" class="text-sm font-medium text-gray-700 cursor-pointer">
                    Rule is active (will run automatically)
                  </label>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    :disabled="isSaving"
                    class="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                    {{ isSaving ? 'Saving...' : 'Save Rule' }}
                  </button>

                  <button
                    type="button"
                    @click="cancelEdit"
                    class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors cursor-pointer">
                    Cancel
                  </button>

                  <button
                    v-if="!isCreatingNew"
                    type="button"
                    @click="deleteRule"
                    class="ml-auto px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-colors cursor-pointer">
                    Delete
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { Plus, Globe, Info, FileText } from 'lucide-vue-next'

export default {
  name: 'EmailRulesManager',
  components: {
    Plus,
    Globe,
    Info,
    FileText
  },
  props: {
    indexPath: {
      type: String,
      required: true
    },
    newPath: {
      type: String,
      required: true
    },
    templateKeys: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      rules: [],
      selectedRule: null,
      isCreatingNew: false,
      isEditing: false,
      isLoading: true,
      isSaving: false,
      errors: [],
      isLoadingPreview: false,
      previewError: null,
      emailPreview: null,
      previewLocale: 'en',
      availableLocales: [],
      showPreview: false,
      previewLoaded: false,
      testRunning: false,
      testResults: null,
      testError: null,
      showTestResults: false,
      formData: {
        template_key: '',
        trigger_type: 'days_after_signup',
        trigger_value: 7,
        condition_type: 'always',
        condition_value: null,
        missing_variant_strategy: 'fallback_to_english',
        active: true
      }
    }
  },
  mounted() {
    this.loadRules()
  },
  methods: {
    async loadRules() {
      try {
        this.isLoading = true
        const response = await fetch(`${this.indexPath}.json`)
        const data = await response.json()
        this.rules = data.rules
      } catch (error) {
        console.error('Failed to load rules:', error)
        this.errors = ['Failed to load rules. Please refresh the page.']
      } finally {
        this.isLoading = false
      }
    },

    selectRule(rule) {
      this.selectedRule = rule
      this.isCreatingNew = false
      this.isEditing = false
      this.errors = []
      this.emailPreview = null
      this.showPreview = false
      this.previewLoaded = false
      this.testResults = null
      this.testError = null
      this.showTestResults = false

      // Populate form with rule data
      this.formData = {
        template_key: rule.template_key,
        trigger_type: rule.trigger_type || 'days_after_signup',
        trigger_value: rule.trigger_value,
        condition_type: rule.condition_type,
        condition_value: rule.condition_value,
        missing_variant_strategy: rule.missing_variant_strategy,
        active: rule.active
      }
    },

    startEditing() {
      this.isEditing = true
      this.testResults = null
      this.showTestResults = false
    },

    createNewRule() {
      this.selectedRule = null
      this.isCreatingNew = true
      this.errors = []
      this.emailPreview = null
      this.showPreview = false
      this.previewLoaded = false

      // Reset form to defaults
      this.formData = {
        template_key: '',
        trigger_type: 'days_after_signup',
        trigger_value: 7,
        condition_type: 'always',
        condition_value: null,
        missing_variant_strategy: 'fallback_to_english',
        active: true
      }
    },

    onTemplateSelected() {
      // Reset preview when template changes
      this.emailPreview = null
      this.availableLocales = []
      this.showPreview = false
      this.previewLoaded = false
    },

    async togglePreview() {
      // If not loaded yet, load it first
      if (!this.previewLoaded && this.formData.template_key) {
        await this.loadTemplateForPreview(this.formData.template_key)
        this.previewLoaded = true
        this.showPreview = true
      } else {
        // Just toggle visibility
        this.showPreview = !this.showPreview
      }
    },

    async saveRule() {
      this.errors = []
      this.isSaving = true

      try {
        const url = this.isCreatingNew
          ? this.newPath.replace('/new', '')
          : `/admin/email_rules/${this.selectedRule.id}`

        const method = this.isCreatingNew ? 'POST' : 'PATCH'

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          body: JSON.stringify({ email_rule: this.formData })
        })

        if (response.ok) {
          await this.loadRules()
          this.isCreatingNew = false
          this.selectedRule = null
        } else {
          const data = await response.json()
          this.errors = data.errors || ['Failed to save rule']
        }
      } catch (error) {
        console.error('Save failed:', error)
        this.errors = ['Network error. Please try again.']
      } finally {
        this.isSaving = false
      }
    },

    async deleteRule() {
      if (!confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
        return
      }

      try {
        const response = await fetch(`/admin/email_rules/${this.selectedRule.id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })

        if (response.ok) {
          await this.loadRules()
          this.selectedRule = null
          this.isCreatingNew = false
        }
      } catch (error) {
        console.error('Delete failed:', error)
        this.errors = ['Failed to delete rule']
      }
    },

    async runTestRun() {
      this.testRunning = true
      this.testError = null
      this.testResults = null
      this.showTestResults = false

      try {
        const response = await fetch(`/admin/email_rules/${this.selectedRule.id}/test_run`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })

        if (response.ok) {
          this.testResults = await response.json()
          this.showTestResults = true
        } else {
          this.testError = 'Test run failed. Please try again.'
        }
      } catch (error) {
        console.error('Test run failed:', error)
        this.testError = 'Test run failed. Please check your connection.'
      } finally {
        this.testRunning = false
      }
    },

    cancelEdit() {
      if (this.isCreatingNew) {
        this.selectedRule = null
        this.isCreatingNew = false
      } else {
        this.isEditing = false
      }
      this.errors = []
    },

    formatCondition(condition) {
      const map = {
        'always': 'Always',
        'has_no_projects': 'No Projects',
        'has_no_network': 'No Network',
        'has_no_wants': 'No Wants',
        'is_free_tier': 'Free Tier',
        'is_test_user': 'Test Users Only',
        'has_draft_older_than_x_days': 'Stale Draft',
        'has_empty_bio': 'Empty Bio'
      }
      return map[condition] || condition
    },

    async loadTemplateForPreview(templateKey) {
      this.isLoadingPreview = true
      this.previewError = null
      this.emailPreview = null

      try {
        // Find template by key
        const response = await fetch(`/admin/email_templates.json?template_key=${templateKey}`)
        if (!response.ok) {
          throw new Error('Failed to find template')
        }

        const data = await response.json()
        const template = data.templates?.find(t => t.template_key === templateKey)

        if (!template) {
          this.previewError = 'Template not found'
          return
        }

        // Get full template with variants
        const templateResponse = await fetch(`/admin/email_templates/${template.id}.json`)
        if (!templateResponse.ok) {
          throw new Error('Failed to load template')
        }

        const templateData = await templateResponse.json()
        this.availableLocales = templateData.variants.map(v => v.locale)
        this.previewLocale = this.availableLocales[0] || 'en'

        await this.loadPreview(templateData)
      } catch (error) {
        console.error('Failed to load template:', error)
        this.previewError = 'Failed to load template'
      } finally {
        this.isLoadingPreview = false
      }
    },

    async loadPreview(templateData = null) {
      if (!templateData) {
        // Reload template data
        await this.loadTemplateForPreview(this.formData.template_key)
        return
      }

      const variant = templateData.variants.find(v => v.locale === this.previewLocale)

      if (variant) {
        this.emailPreview = {
          subject: variant.subject,
          body: variant.body // FULL BODY, not truncated
        }
      } else {
        this.previewError = `No ${this.previewLocale.toUpperCase()} variant available`
      }
    }
  }
}
</script>
